import { partials } from '@shopify/partial-rendering';
import { CollectionUpdateEvent, SearchUpdateEvent } from '@shopify/standard-events';
import { transition } from 'view-transition';
import { announceGlobalLiveRegion } from './live-region.js';

/**
 * <s-live-form> — Wraps a <form> and re-fetches one or more partials on every input/change
 * event, keeping filter, sort, and search results in sync without a full page reload.
 *
 * Attributes (on <s-live-form>):
 *   partial    — Comma-separated default list of {% partial 'name' %} region names to
 *                fetch/apply. Must exactly match the partial wrappers in
 *                templates/collection.liquid, templates/search.liquid, or
 *                blocks/predictive-search.liquid — renaming either side silently breaks
 *                live updates.
 *   data-query-partial — Optional comma-separated partial override used when input[name="q"]
 *                        changes. Search uses this to refresh its outer results shell only
 *                        for new queries while filter-only changes keep the drawer mounted.
 *   data-clear-filters-on-query-change — Optional boolean attribute; when present, q changes
 *                                       drop filter.* params from the next request URL.
 *   data-no-url-sync — Uses the form action as the request URL base without changing browser
 *                      history. User-initiated submits continue as native navigation.
 *   native-submit — If present, changes still request-submit the form, but the submit is
 *                   allowed to continue natively. Use for Shopify POST forms like localization.
 *   data-native-action — Optional absolute action URL applied just before native submission.
 *   debounce   — Debounce delay in ms for text `input` events (default 0 = immediate).
 *   data-updated-message — i18n string announced via #sr-announcer on success.
 *   data-error-message   — i18n string announced via #sr-announcer on fetch failure.
 *
 * Required child markup:
 *   form                      — The form element driving submissions; required.
 *   [name]                    — Any named input/select/checkbox whose values are serialized
 *                               into the request URL. `name` must match the URL param the
 *                               server-side Liquid template accepts (e.g. `q`, `sort_by`,
 *                               `filter.v.*`) — renaming breaks round-trip state rehydration.
 *   [data-sync-name]          — Optional visible proxy control that mirrors a named control
 *                               with the same URL param (used for mobile sort inside drawers).
 *
 * Update triggers:
 *   input event on text inputs — Debounced by `debounce` ms (0 = immediate). Keeps typing in
 *                                q and price-range fields to one fetch/replaceState per pause
 *                                instead of one per keystroke.
 *   change on select/checkbox  — Immediate form.requestSubmit(); cancels any pending
 *                                debounced text submit so the immediate update carries the
 *                                typed value without a trailing duplicate fetch.
 *   change on text inputs      — Commit points (focus-out/Enter) flush a pending debounced
 *                                submit immediately; no-op when the debounce already fired.
 *   [data-clear-search] press  — Cancels the pending debounced submit at pointerdown (ahead
 *                                of the focus-out change-commit flush) and again on click, then
 *                                clears input[name="q"] and re-dispatches input. For pointer
 *                                activation and focus-preserving activation (AT/synthetic click
 *                                with no prior focus shift out of an edited q), only the cleared
 *                                state fetches — never the abandoned typed query. Keyboard
 *                                Tab→Enter is a two-step commit: Tabbing off an edited q fires
 *                                the change-commit flush (fetching the typed value) before the
 *                                button can cancel it, then Enter fetches the cleared state.
 *                                Two fetches, correct cleared final state — baseline-equivalent.
 *   form submit                — Cancels any pending debounced submit, then: remote-action
 *                                forms get native navigation on user-initiated submits;
 *                                partial rendering on programmatic submits. Query changes can
 *                                fetch data-query-partial and clear filters.
 *
 * Dispatches:
 *   s-live-form:updated — Bubbles after a successful partial apply.
 *
 * External coupling:
 *   aria-busy attribute (on self) — Set to "true" during fetch, removed on completion.
 *   history.replaceState          — URL updated with serialized form params (local forms only).
 *   #sr-announcer (global)        — Live region for success/error announcements.
 */
// The `partial` and `data-query-partial` HTML attributes are comma-separated lists of
// {% partial 'name' %} region names in the server-rendered page. These strings are a naming
// contract: renaming a partial on either the Liquid side (templates/collection.liquid,
// templates/search.liquid, blocks/predictive-search.liquid) or here will silently break
// partial-rendering updates.
class LiveForm extends HTMLElement {
  #timer;
  #programmatic = false;
  #ac;
  #abortController;
  #previousQuery;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    const debounce = parseInt(this.getAttribute('debounce'), 10) || 0;
    const nativeSubmit = this.hasAttribute('native-submit');
    const form = this.querySelector('form');

    // Rehydration: each form input's `name` attribute must exactly match the URL param the
    // server-side Liquid template accepts (e.g. `q`, `sort_by`, `filter.v.*`).
    // Renaming any input `name` here, or the corresponding Liquid filter/sort param, breaks
    // round-trip state — the input will not be restored when the user navigates back.
    const params = new URLSearchParams(window.location.search);
    for (const el of this.querySelectorAll('[name]')) {
      if (el.type === 'checkbox') {
        const values = params.getAll(el.name);
        el.checked = values.includes(el.value);
      } else {
        this.#applyRehydratedValue(el, params.get(el.name));
      }
    }

    for (const el of this.querySelectorAll('[data-sync-name]')) {
      const name = el.dataset.syncName;
      if (!name) continue;
      let value = params.get(name);
      if (value == null) {
        value = this.#namedControls(name).find((control) => 'value' in control)?.value;
      }
      this.#applyRehydratedValue(el, value);
    }

    // Query-change behavior belongs to this form's state, not browser history that another
    // component or delayed callback can update independently.
    this.#previousQuery = this.#queryValue(new FormData(form));

    // Single update path — all events funnel through form submit
    form.addEventListener('submit', (e) => {
      // A submit supersedes any pending debounced text edit; without this an
      // Enter-committed value would fetch again when the stale timer fired.
      this.#clearTimer();
      if (nativeSubmit) {
        this.#programmatic = false;
        if (this.dataset.nativeAction) {
          form.action = this.dataset.nativeAction;
        }
        return;
      }

      // For remote forms (action differs from current page), let user-initiated
      // submits (Enter key) navigate natively. Programmatic submits from
      // input/change handlers still do partial rendering.
      if (!this.#programmatic) {
        const action = form.getAttribute('action');
        const isRemote = action && new URL(action, location.origin).pathname !== location.pathname;
        if (isRemote || this.hasAttribute('data-no-url-sync')) return;
      }
      this.#programmatic = false;
      e.preventDefault();
      this.#update();
    }, opts);

    // Text inputs: debounced
    this.addEventListener('input', (e) => {
      const target = e.target;
      if (!(target instanceof HTMLElement)) return;
      if (target.dataset.syncName) this.#syncProxyControl(target);
      // Selects and checkboxes submit from the change handler below.
      if (target.matches('select, input[type="checkbox"]')) return;
      this.#clearTimer();
      if (debounce) {
        this.#timer = setTimeout(() => {
          this.#timer = null;
          this.#programmatic = true;
          form.requestSubmit();
        }, debounce);
        return;
      }

      this.#programmatic = true;
      form.requestSubmit();
    }, opts);

    // Selects & checkboxes: immediate. Text inputs: `change` (focus-out/Enter commit)
    // flushes a pending debounced submit so committed edits apply without waiting.
    this.addEventListener('change', (e) => {
      const target = e.target;
      if (!(target instanceof HTMLElement)) return;
      if (target.dataset.syncName) this.#syncProxyControl(target);
      if (target.matches('[name]') && 'value' in target) {
        this.#syncProxyControls(target.getAttribute('name'), target.value);
      }
      if (target.matches('select, input[type="checkbox"]')) {
        this.#clearTimer();
        this.#programmatic = true;
        form.requestSubmit();
        return;
      }
      // Only flush when a debounce timer is still pending; otherwise the
      // debounced submit already ran and losing focus must not fetch again.
      if (this.#timer != null) {
        this.#clearTimer();
        this.#programmatic = true;
        form.requestSubmit();
      }
    }, opts);

    // Clear button: resets the search input and re-triggers the input handler
    // (preserving the debounce/fetch flow). Scoped via `[data-clear-search]`
    // so consumers opt in declaratively instead of wiring inline onclick.
    // Pressing clear abandons the typed query: cancel the pending debounce at
    // pointerdown, before the focus shift fires the change-commit flush, so
    // the abandoned value never fetches ahead of the cleared submit below.
    this.addEventListener('pointerdown', (e) => {
      if (!e.target.closest('[data-clear-search]')) return;
      this.#clearTimer();
    }, opts);

    this.addEventListener('click', (e) => {
      if (!e.target.closest('[data-clear-search]')) return;
      const input = form.querySelector('input[name="q"]');
      if (!input) return;
      // Keyboard/AT activation skips pointerdown: cancel any still-pending
      // debounce before dispatching the cleared input so the abandoned query
      // cannot submit. Note: when keyboard focus departs an edited q (Tab to
      // this button), the change-commit flush has already fetched the typed
      // value before this handler can cancel — the clear then fetches the
      // cleared state as a second, final step.
      this.#clearTimer();
      input.value = '';
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }, opts);

  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#abortController?.abort();
    this.#clearTimer();
  }

  // #timer doubles as pending-debounce state: non-null only while a debounced
  // submit is scheduled, so a change commit can tell "flush now" from "already ran".
  #clearTimer() {
    clearTimeout(this.#timer);
    this.#timer = null;
  }

  #syncProxyControl(control) {
    const name = control.dataset.syncName;
    if (!name || !('value' in control)) return;
    for (const target of this.#namedControls(name)) {
      if ('value' in target) target.value = control.value;
    }
  }

  #syncProxyControls(name, value) {
    if (!name) return;
    for (const control of this.querySelectorAll('[data-sync-name]')) {
      if (control instanceof HTMLElement && control.dataset.syncName === name && 'value' in control) {
        control.value = value;
      }
    }
  }

  #namedControls(name) {
    return [...this.querySelectorAll('[name]')].filter((control) => control.getAttribute('name') === name);
  }

  // URLSearchParams has already decoded the param, so encoded punctuation such
  // as %26 ("&") is legitimate data — restore it verbatim, never truncate it.
  // For selects, only apply values that match an option so a malformed URL
  // cannot blank the server-rendered selection.
  #applyRehydratedValue(el, value) {
    if (value == null) return;
    const stringValue = String(value);
    if (!stringValue.trim()) return;
    if (el instanceof HTMLSelectElement && ![...el.options].some((option) => option.value === stringValue)) {
      return;
    }
    el.value = stringValue;
  }

  async #update() {
    // Abort stale response so fast typing can't let an earlier result overwrite a fresher one
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController; // capture before any yield

    const form = this.querySelector('form');
    const action = form?.getAttribute('action');
    const isRemote = action && new URL(action, location.origin).pathname !== location.pathname;
    const syncUrl = !isRemote && !this.hasAttribute('data-no-url-sync');
    const url = syncUrl ? new URL(window.location) : new URL(action || window.location.href, location.origin);
    const data = form ? new FormData(form) : null;
    const query = this.#queryValue(data);
    const queryChanged = query != null && query !== this.#previousQuery;
    const clearFilterParams = queryChanged && this.hasAttribute('data-clear-filters-on-query-change');

    // Clean up any existing form-related parameters first
    for (const el of this.querySelectorAll('[name]')) {
      url.searchParams.delete(el.name);
    }
    // Native GET submission replaces the whole query string, resetting pagination.
    // Mirror that so filter/sort changes never fetch a stale page of the new result
    // set (a form control named "page" would be re-appended below).
    url.searchParams.delete('page');
    if (clearFilterParams) this.#deleteParamsByPrefix(url.searchParams, 'filter.');

    // Use FormData for proper serialization (handles checkboxes, multi-values)
    if (form) {
      for (const [name, value] of data) {
        if (clearFilterParams && name.startsWith('filter.')) continue;
        const v = String(value).trim();
        if (v) url.searchParams.append(name, v);
      }
    } else {
      // Fallback for non-form elements
      for (const el of this.querySelectorAll('[name]')) {
        if (clearFilterParams && el.name?.startsWith('filter.')) continue;
        const value = el.value.trim();
        if (value) {
          url.searchParams.set(el.name, value);
        }
      }
    }

    const attr = this.#partialAttributeForUpdate(queryChanged);
    if (!attr) return;

    if (syncUrl) {
      // Yield a task so the browser can paint before the URL update — and so a
      // change-flush + implicit-submit pair (Enter commit) intentionally aborts
      // this pass via `signal`, coalescing to one fetch/replaceState.
      await new Promise(resolve => setTimeout(resolve, 0));
      if (signal.aborted) return;
      history.replaceState(null, '', url);
    }

    this.setAttribute('aria-busy', 'true');

    const pendingEvent = this.#dispatchStandardEvent(url);

    try {
      const names = attr.split(/\s*,\s*/);
      // @partial-fetch-contract attributes=partial,data-query-partial
      const html = await partials.fetch(...names, { url: url.toString(), signal });
      await transition(() => partials.apply(html));
      if (query != null) this.#previousQuery = query;

      this.removeAttribute('aria-busy');
      pendingEvent?.resolve();
      this.dispatchEvent(new CustomEvent('s-live-form:updated', { bubbles: true }));

      announceGlobalLiveRegion(this.dataset.updatedMessage);
    } catch (error) {
      pendingEvent?.reject(error);
      if (error.name === 'AbortError') return;
      this.removeAttribute('aria-busy');
      console.error('[s-live-form] Partial fetch failed:', error);
      announceGlobalLiveRegion(this.dataset.errorMessage);
    }
  }

  #queryValue(data) {
    const query = data?.get('q');
    return query == null ? null : String(query).trim();
  }

  #partialAttributeForUpdate(queryChanged) {
    if (queryChanged && this.dataset.queryPartial) return this.dataset.queryPartial;
    return this.getAttribute('partial');
  }

  #deleteParamsByPrefix(searchParams, prefix) {
    for (const key of [...searchParams.keys()]) {
      if (key.startsWith(prefix)) searchParams.delete(key);
    }
  }

  #dispatchStandardEvent(url) {
    const isCollection = this.dataset.collectionId && this.dataset.collectionHandle;
    const isPredictive = !isCollection && !!this.closest('s-predictive-search');

    if (isCollection) {
      const deferredPromise = CollectionUpdateEvent.createPromise();
      const productFilters = CollectionUpdateEvent.parseProductFilters(url.searchParams);

      this.dispatchEvent(
        new CollectionUpdateEvent({
          collection: {
            id: this.dataset.collectionId,
            handle: this.dataset.collectionHandle,
            productsCount: this.#getResultsCount(),
          },
          ...(productFilters && { productFilters }),
          sortKey: CollectionUpdateEvent.getSortKey(url.searchParams),
          promise: deferredPromise.promise,
        })
      );

      return {
        resolve: () => deferredPromise.resolve({ productsCount: this.#getResultsCount() }),
        reject: (error) => deferredPromise.reject(error),
      };
    }

    const query = url.searchParams.get('q')?.trim() || '';
    if (!query) return;

    const deferredPromise = SearchUpdateEvent.createPromise();
    const productFilters = isPredictive
      ? undefined
      : SearchUpdateEvent.parseProductFilters(url.searchParams);

    this.dispatchEvent(
      new SearchUpdateEvent({
        search: {
          query,
          ...(productFilters && { productFilters }),
          sortKey: isPredictive ? undefined : SearchUpdateEvent.getSortKey(url.searchParams),
        },
        promise: deferredPromise.promise,
      })
    );

    return {
      resolve: () => deferredPromise.resolve({ totalCount: this.#getResultsCount() }),
      reject: (error) => deferredPromise.reject(error),
    };
  }

  #getResultsCount() {
    const marker = this.querySelector('[data-results-count]');
    return Number(marker?.textContent || 0);
  }
}
customElements.define('s-live-form', LiveForm);
